import { useState } from "react";
import { ExternalLink, Instagram, Loader2, Send, ShieldQuestion, Youtube } from "lucide-react";
import { toast } from "sonner";
import { StarButton } from "@/components/Stars";
import { ViewContact } from "@/components/ViewContact";
import { TIER_COLORS } from "@/components/LiveIntelTable";
import { BotMeter, BOT_HELP } from "@/components/BotCheck";
import { Exchanges, AV_ICON } from "@/components/c360/C360Table";
import { RATING, short } from "@/lib/creator360";
import { bl, useBlur } from "@/lib/c360Mine";
import { MainProfileButton } from "@/components/live/LiveHeader";
import { VIEW_LABEL, agoWords, comma, liveLast, num1, streamingPill } from "@/lib/c360Profile";
import { LEVEL_COLOR, WHY, bought, estimateLine, levelWord, openBots, openedBots, useOpenedBots } from "@/lib/botReasons";

// v80 — the helper line per level; a missing check says plainly why (lib/botReasons), never how.
const BOT_TEXT = {
  live: { none: "Nothing unusual found. The live crowd looks like a real audience.", low: "Mostly fine. A small oddity in the live numbers, but the crowd looks mostly real.", medium: "Part of the live viewer count looks inflated. Treat the live number with caution.", high: "Most of the live viewer count looks inflated. Go by chat and views, not the live count." },
  ig: { none: "Nothing unusual found. The follower count looks like a real audience.", low: "Mostly fine. Their reels reach a bit fewer of their followers than usual, but most of the audience looks real. Worth a quick look at recent reels before a big deal.", medium: "A significant part of the follower count looks inflated or inactive. Treat the number with caution and go by the likely real audience shown.", high: "Most of the follower count looks inflated or inactive. Go by reel views and engagement, not by followers." },
  tg: { none: "Nothing unusual found. The subscriber count looks like a real audience.", low: "Mostly fine. Their posts reach a bit fewer of their subscribers than usual, but most of the audience looks real. Worth a quick look at recent posts before a big deal.", medium: "A significant part of the subscriber count looks inflated or inactive. Treat the number with caution and go by the likely real audience shown.", high: "Most of the subscriber count looks inflated or inactive. Go by views per post, not by subscribers." },
  yt: BOT_HELP,
};
const BOT_TITLE = { yt: "YouTube bot check", live: "Live bot check", ig: "Instagram bot check", tg: "Telegram bot check" };

export function botHelp(av, b) {
  if (b.why) return WHY[b.why];
  return bought(av, b) || BOT_TEXT[av][b.level] || "";
}

export function BotCard({ av, b, testid = `c360-botcard-${av}`, wide = false }) {
  const lv = b?.level || "not_checked";
  const est = estimateLine(av, b);
  const muted = lv === "missing";
  return (
    <div className={`border hairline rounded-lg p-4 w-full ${wide ? "" : "lg:w-[340px]"} ${muted ? "opacity-60" : ""}`} style={{ background: "var(--surface-2)" }} data-testid={testid} data-level={lv}>
      <div className="flex items-baseline justify-between gap-3">
        <span className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{BOT_TITLE[av]}</span>
        <span className="mono text-xs" style={{ color: muted ? "#71717A" : LEVEL_COLOR[lv] === "#52525B" ? "#A1A1AA" : "#fff" }} data-testid={`${testid}-word`}>{levelWord(b)}</span>
      </div>
      {!muted && <div className="mt-3"><BotMeter verdict={lv} /></div>}
      <p className="text-xs text-zinc-400 leading-snug mt-3" data-testid={`${testid}-help`}>{botHelp(av, b || { level: lv })}</p>
      {est && <p className="text-xs leading-snug mt-1.5" style={{ color: LEVEL_COLOR[lv] }} data-testid={`${testid}-est`}>{est}</p>}
    </div>
  );
}

// v80 — for a client the card is collapsed behind a click; each open is recorded as a Bot Check open.
function LockedBot({ av, id }) {
  const [busy, setBusy] = useState(false);
  const open = async () => {
    setBusy(true);
    try { await openBots(id); } catch { toast.error("Could not open the bot check"); } finally { setBusy(false); }
  };
  return (
    <button type="button" onClick={open} disabled={busy} data-testid={`c360-botcard-${av}-open`}
      className="border hairline rounded-lg p-4 w-full lg:w-[340px] text-left flex items-center justify-between gap-3 hover:border-white/30 transition-colors" style={{ background: "var(--surface-2)" }}>
      <span className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500">{BOT_TITLE[av]}</span>
      <span className="inline-flex items-center gap-1.5 text-sm text-zinc-200">{busy ? <Loader2 size={13} className="animate-spin" /> : <ShieldQuestion size={14} className="text-orange-300" />} View bot check</span>
    </button>
  );
}

export function useBots(d) {
  useOpenedBots();
  return d.bot || openedBots(d.creator.id) || null;
}

function BotSlot({ av, d }) {
  const bots = useBots(d);
  if (!d.bot_card) return null;
  if (!bots) return d.bot_locked ? <LockedBot av={av} id={d.creator.id} /> : null;
  return <BotCard av={av} b={bots[av]} />;
}

const TILE_NOTE = { yt: "not read yet", live: "does not go live", ig: "no account found", tg: "no channel found" };
export function AudienceTiles({ d, onOpen }) {
  const n = d.nums;
  const lastStd = agoWords(d.nums.yt.last_date ? Math.round((new Date(d.today) - new Date(d.nums.yt.last_date)) / 864e5) : null);
  const tiles = [
    ["yt", "YouTube", n.yt.subs, "subscribers", lastStd ? `last standard video ${lastStd}` : null],
    ["live", "YouTube live streams", n.live.viewers, "typical live viewers", streamingPill(d)],
    ["ig", "Instagram", n.ig.followers, "followers", n.ig.rpw != null ? `${num1(n.ig.rpw)} reels a week` : null],
    ["tg", "Telegram", n.tg.subs, (n.tg.type || "").toLowerCase() === "group" ? "members" : "subscribers", n.tg.last_days != null ? `last post ${agoWords(n.tg.last_days)}` : null],
  ];
  return (
    <div className="w-full lg:w-[420px]" data-testid="c360-audience">
      <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-2">Their audience on each platform</div>
      <div className="grid grid-cols-2 gap-2">
        {tiles.map(([av, name, v, unit, sub]) => {
          const r = RATING[d.av[av].r];
          const Icon = AV_ICON[av];
          const none = !v || (av === "live" && d.av.live.r === "unknown" && !v);
          return (
            <button key={av} type="button" onClick={() => onOpen(av)} data-testid={`c360-aud-${av}`} data-rating={d.av[av].r}
              className="text-left rounded-md p-3 border transition-transform duration-150 hover:-translate-y-0.5" style={{ borderColor: `${r.color}55`, background: `${r.color}0d` }}>
              <div className="flex items-center gap-1.5 text-[11px] text-zinc-400"><Icon size={12} style={{ color: r.color }} />{name}</div>
              <div className="text-lg text-white font-semibold mt-1">{none ? "—" : av === "live" ? comma(v) : short(v)} <span className="text-[11px] text-zinc-500 font-normal">{none ? (av === "live" && d.av.live.r === "unknown" && d.nums.live.group !== "never" ? "not measured yet" : TILE_NOTE[av]) : unit}</span></div>
              {!none && sub && <div className="mono text-[10px] text-zinc-500 mt-0.5">{sub}</div>}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const pillCls = "mono text-[10px] h-6 px-2 rounded inline-flex items-center border";

function Pills({ view, d }) {
  const c = d.creator;
  const n = d.nums;
  const yt = `${short(n.yt.subs)} YouTube subscribers`;
  const out = [];
  if (view === "yt" || view === "live" || view === "best") out.push(<span key="t" className={pillCls} style={{ color: TIER_COLORS[c.tier], borderColor: `${TIER_COLORS[c.tier]}66` }} data-testid="c360-pill-tier">Tier {c.tier}</span>);
  if (view === "ig" && n.ig.followers) out.push(<span key="f" className={`${pillCls} text-zinc-200 border-white/15`}>{short(n.ig.followers)} Instagram followers</span>);
  if (view === "tg" && n.tg.subs) out.push(<span key="s" className={`${pillCls} text-zinc-200 border-white/15`}>{short(n.tg.subs)} Telegram {(n.tg.type || "").toLowerCase() === "group" ? "members" : "subscribers"}</span>);
  out.push(<span key="y" className={`${pillCls} text-zinc-200 border-white/15`} data-testid="c360-pill-yt">{yt}</span>);
  if (view === "ig" && n.ig.verified) out.push(<span key="v" className={pillCls} style={{ color: "#00F0FF", borderColor: "#00F0FF66" }} data-testid="c360-pill-verified">Verified</span>);
  if (view === "tg" && n.tg.type) out.push(<span key="ty" className={`${pillCls} text-zinc-300 border-white/15`} data-testid="c360-pill-tgtype">{n.tg.type}</span>);
  const sp = view === "live" && streamingPill(d);
  if (sp) out.push(<span key="sp" className={pillCls} style={sp.startsWith("Frequent") ? { color: "#39FF14", borderColor: "#39FF1466" } : { color: "#00F0FF", borderColor: "#00F0FF66" }} data-testid="c360-pill-streaming">{sp}</span>);
  if (view === "live" && liveLast(d) === "Live now") out.push(<span key="ln" className={pillCls} style={{ color: "#F43F5E", borderColor: "#F43F5E66" }}>Live now</span>);
  return <div className="flex flex-wrap gap-1.5 mt-3">{out}</div>;
}

const handle = (url) => { const m = String(url || "").match(/instagram\.com\/([^/?#]+)/); return m ? `@${m[1]}` : null; };
const linkCls = "inline-flex items-center gap-2 px-3 py-1.5 rounded-md border hairline text-sm hover:border-white/30 transition-colors";

export function Header({ view, d, star, onOpen, onMain }) {
  const c = d.creator;
  const sub = view === "ig" ? handle(c.ig) : view === "tg" && c.tg ? c.tg.replace(/^https:\/\//, "") : null;
  const blur = useBlur();
  const ep = c.seg === "unaffiliated" ? `/unaffiliated/creator/${c.id}/contact` : `/kols/${c.id}/contact`;
  return (
    <div className="flex flex-col lg:flex-row gap-6 justify-between" data-testid={`c360-header-${view}`}>
      <div className="min-w-0">
        <div className="mono text-[10px] uppercase tracking-[0.3em] text-cyan-400" data-testid="c360-view-label">{VIEW_LABEL[view]}</div>
        <div className="flex items-center gap-2 mt-2">
          <StarButton on={star.on} onToggle={star.toggle} testid="c360-profile-star" size={18} />
          <h2 className="text-2xl sm:text-3xl font-semibold text-white tracking-tight truncate" data-testid="c360-profile-name"><span className={bl(blur)}>{c.name}</span></h2>
        </div>
        {sub && <div className="mono text-xs text-zinc-400 mt-1" data-testid="c360-profile-handle"><span className={bl(blur)}>{sub}</span></div>}
        <Pills view={view} d={d} />
        <div className="mt-4">
          <div className="mono text-[10px] uppercase tracking-[0.25em] text-zinc-500 mb-1.5">Promoted on YouTube</div>
          <Exchanges r={{ id: c.id, ex: d.ex }} />
        </div>
        <div className="flex flex-wrap gap-2 mt-4">
        <div className={`flex flex-wrap gap-2 ${bl(blur, "link")}`} data-testid="c360-profile-links">
          {c.yt && <a href={c.yt} target="_blank" rel="noreferrer" className={linkCls} data-testid="c360-link-yt"><Youtube size={14} className="text-red-400" /> YouTube <ExternalLink size={11} className="text-zinc-500" /></a>}
          {c.tg && <a href={c.tg} target="_blank" rel="noreferrer" className={linkCls} data-testid="c360-link-tg"><Send size={14} className="text-cyan-400" /> Telegram <ExternalLink size={11} className="text-zinc-500" /></a>}
          {c.ig && <a href={c.ig} target="_blank" rel="noreferrer" className={linkCls} data-testid="c360-link-ig"><Instagram size={14} className="text-fuchsia-400" /> Instagram <ExternalLink size={11} className="text-zinc-500" /></a>}
          <ViewContact endpoint={ep} channelId={c.id} place={d.admin ? null : "c360"} testid="c360-view-contact" render={(x) => (<>
            {x.contact_email && <a href={`mailto:${x.contact_email}`} className={linkCls} data-testid="c360-contact-email">{x.contact_email}</a>}
            {x.contact_phone && <a href={`tel:${x.contact_phone}`} className={linkCls} data-testid="c360-contact-phone">{x.contact_phone}</a>}
          </>)} />
        </div>
        {onMain && <MainProfileButton onClick={onMain} testid="c360-main-profile" className={linkCls} />}
        </div>
      </div>
      {view === "best" ? <AudienceTiles d={d} onOpen={onOpen} /> : <BotSlot av={view} d={d} />}
    </div>
  );
}
